import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { prefetchPost, usePost } from '../../api/queries'
import { useCardKeys, useSwipe } from '../../lib/cardControls'
import { downloadFile } from '../../lib/download'
import { loadMoreNear, neighbours, useFeedOrder } from '../../lib/feedNav'
import { feedMeasurement } from '../../lib/feedNav'
import { postHref, postMeasurement } from '../../lib/measurement'
import { postFileName } from '../../lib/format'
import { BackLayer, useBack, useBackHandler } from '../../lib/useBack'
import { useIsDesktop } from '../../lib/useIsDesktop'
import { useScrollLock } from '../../lib/useScrollLock'
import { useKeyboardBoundary } from '../../lib/useKeyboardBoundary'
import { hasTelegramBack } from '../../telegram'
import { Button, buttonClass } from '../Button'
import { PostCard } from './PostCard'

/**
 * Карточка поверх ленты: окно на компьютере, весь экран на телефоне и в Mini App.
 * Она — слой стека «Назад»: Esc и «Назад» Telegram закрывают её, а если поверх
 * открыт лист (♥, добавление источника) — сначала лист.
 */
export function PostOverlay() {
  const { id } = useParams()
  const postId = Number(id)
  const keyboardBoundary = useKeyboardBoundary(postId)
  const navigate = useNavigate()
  const location = useLocation()
  const desktop = useIsDesktop()
  const qc = useQueryClient()
  const order = useFeedOrder()
  const { prev, next } = neighbours(order, postId)
  const position = order.indexOf(postId)
  const measurement = postMeasurement(new URLSearchParams(location.search))
  const post = usePost(postId, measurement)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  const close = useBack('/')
  const go = useCallback(
    (target: number | null) => {
      if (target === null) return
      void navigate(postHref({ id: target }, feedMeasurement(target) ?? measurement), { replace: true, state: location.state })
    },
    [navigate, location.state, location.search],
  )

  const layer = useBackHandler(close)
  useScrollLock()

  // Соседей грузим заранее, чтобы листание было мгновенным; у конца — следующую страницу ленты.
  useEffect(() => {
    if (prev !== null) prefetchPost(qc, prev, feedMeasurement(prev) ?? measurement)
    if (next !== null) prefetchPost(qc, next, feedMeasurement(next) ?? measurement)
    loadMoreNear(order, postId)
  }, [qc, prev, next, order, postId, location.search])

  const data = post.data
  useCardKeys(layer, {
    prev: () => go(prev),
    next: () => go(next),
    download: data?.download_url ? () => downloadFile(data.download_url ?? '', postFileName(data)) : null,
  })
  const swipe = useSwipe((direction) => go(direction === 'next' ? next : prev))

  const counter = position >= 0 ? `${position + 1} из ${order.length}` : null

  if (desktop) {
    const visual = post.data?.platform === 'instagram' || post.isPending
    return (
      <BackLayer.Provider value={layer}>
        <div ref={keyboardBoundary} className="fixed inset-0 z-40 flex items-center justify-center p-6 xl:px-20">
          <div className="absolute inset-0 bg-black/65" onClick={close} aria-hidden />
          <NavArrow side="left" disabled={prev === null} onClick={() => go(prev)} />
          <NavArrow side="right" disabled={next === null} onClick={() => go(next)} />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Пост"
            className={`relative flex h-[min(88vh,900px)] w-full overflow-hidden rounded-2xl bg-card shadow-2xl ring-1 ring-line ${
              visual ? 'max-w-[1080px]' : 'max-w-2xl'
            }`}
          >
            <PostCard
              id={postId}
              layout="split"
              videoRef={videoRef}
              extraActions={
                <Button size="icon" variant="ghost" aria-label="Закрыть (Esc)" title="Закрыть (Esc)" onClick={close}>
                  <X size={18} />
                </Button>
              }
            />
          </div>
          {counter && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-white/70 tabular-nums">
              {counter} · ← → листать · F в избранное · D скачать · Esc закрыть
            </div>
          )}
        </div>
      </BackLayer.Provider>
    )
  }

  return (
    <BackLayer.Provider value={layer}>
      <div ref={keyboardBoundary} className="fixed inset-0 z-40 flex flex-col bg-bg">
        <header className="border-b border-line bg-chrome-top pt-safe">
          <div className="flex h-12 items-center gap-1 px-safe">
            {!hasTelegramBack() && (
              <button
                type="button"
                onClick={close}
                aria-label="Назад"
                className="-ml-3 inline-flex size-11 items-center justify-center rounded-full active:bg-surface"
              >
                <ChevronLeft size={24} />
              </button>
            )}
            <h1 className="min-w-0 flex-1 truncate text-[17px] font-semibold tracking-tight">Пост</h1>
            {counter && (
              <div className="-mr-2 flex items-center">
                <button
                  type="button"
                  className={buttonClass('ghost', 'icon')}
                  aria-label="Предыдущий пост"
                  disabled={prev === null}
                  onClick={() => go(prev)}
                >
                  <ChevronLeft size={22} />
                </button>
                <span className="min-w-14 text-center text-sm text-muted tabular-nums" aria-live="polite">
                  {counter}
                </span>
                <button
                  type="button"
                  className={buttonClass('ghost', 'icon')}
                  aria-label="Следующий пост"
                  disabled={next === null}
                  onClick={() => go(next)}
                >
                  <ChevronRight size={22} />
                </button>
              </div>
            )}
          </div>
        </header>
        <div data-view-scroll={`post.${postId}.body`} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-safe pt-4 pb-[calc(1rem+var(--safe-bottom))]" {...swipe}>
          <PostCard id={postId} layout="stack" videoRef={videoRef} />
        </div>
      </div>
    </BackLayer.Provider>
  )
}

function NavArrow({ side, disabled, onClick }: { side: 'left' | 'right'; disabled: boolean; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={side === 'left' ? 'Предыдущий пост (←)' : 'Следующий пост (→)'}
      className={`absolute top-1/2 z-10 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-black shadow-lg transition-opacity hover:bg-white disabled:pointer-events-none disabled:opacity-0 ${
        side === 'left' ? 'left-3 xl:left-6' : 'right-3 xl:right-6'
      }`}
    >
      <Icon size={22} />
    </button>
  )
}
